import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { type UseFormReturn, useForm } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import { actions, services, useSubmit } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import { scrollToTop, serviceConvert } from 'utils';

import { formatCreditInfo } from './helpers';
import useApplySubmit from './useApplySubmit';

import { ApplyFormFieldValues, useApplyContext } from '../context';

export interface ApplyYourInfoFormHook {
  form: UseFormReturn<ApplyFormFieldValues>;
  onSubmit: (formValues: StringIndexedObject<ApplyFormFieldValues>) => void;
  cacheKey: string | undefined;
  showForm: boolean;
  success: boolean;
  id: string | undefined;
  showButton: boolean;
  isSubmitting: boolean;
}

export default function useApplyYourInfoForm(): ApplyYourInfoFormHook {
  const {
    isLoading,
    id,
    job,
    rate,
    creditId,
    introduction,
    isExampleJob,
    isIntroduced,
    cache,
    cacheKey,
  } = useApplyContext();

  const { isSubmitting, submitHandler } = useApplySubmit();
  const { push } = useHistory();

  const [showForm, setShowForm] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [showButton, setShowButton] = useState<boolean>(false);

  const { submit: putCache } = useSubmit(
    serviceConvert(services.infoCache.infoPutCache),
  );
  const dispatch = useAppDispatch();

  const defaultValues = useMemo(() => {
    if (isLoading) return {};
    return formatCreditInfo(
      job,
      introduction,
      isExampleJob,
      isIntroduced,
      cache,
      cacheKey,
    );
  }, [
    isLoading,
    job,
    introduction,
    isExampleJob,
    isIntroduced,
    cache,
    cacheKey,
  ]);

  const form = useForm<ApplyFormFieldValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const deleteCache = useCallback(() => {
    if (!cache || !cacheKey) return;
    putCache({
      body: {
        ...cache,
        [cacheKey]: '',
      },
    });
  }, [putCache, cache, cacheKey]);

  // Refs to hold timers
  const timerRef = useRef<NodeJS.Timer | null>(null);
  const fallbackTimerRef = useRef<NodeJS.Timer | null>(null);

  // Function to redirect through timers
  const redirect = useCallback(() => {
    if (!id) return;
    dispatch(
      actions.getJob({
        params: { id },
        forceReload: true,
        onError: () => {},
      }),
    ).then(() => {
      // Redirect after 5 seconds
      timerRef.current = setTimeout(
        () => push(generatePath(URLS.status, { id })),
        5000,
      );
      // Show button after 10s if not redirected
      fallbackTimerRef.current = setTimeout(() => setShowButton(true), 10000);
    });
  }, [push, id, dispatch]);

  const successCallback = useCallback(() => {
    setSuccess(true);
    deleteCache();
    redirect();
    scrollToTop();
  }, [deleteCache, redirect]);

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<ApplyFormFieldValues>) => {
      if (!rate || !creditId) return;
      submitHandler(
        creditId,
        formValues as unknown as ApplyFormFieldValues,
        rate,
        successCallback,
      );
    },
    [rate, creditId, submitHandler, successCallback],
  );

  const { reset } = form;

  const addDefaultValues = useCallback(() => {
    reset(defaultValues);
    setTimeout(() => setShowForm(true), 100);
  }, [reset, defaultValues]);

  const setRef = useRef<boolean>(false);
  useEffect(() => {
    if (isLoading || setRef.current) return;
    reset(defaultValues);
    setRef.current = true;
    setTimeout(() => addDefaultValues(), 100);
  }, [isLoading, addDefaultValues, reset, defaultValues]);

  return {
    form,
    onSubmit,
    cacheKey,
    showForm,
    success,
    id,
    showButton,
    isSubmitting,
  };
}
